/**
 * @file DesktopNotificationBridge.tsx
 * @brief Desktop app only: hands the sign-in to the app's main process so it can show native notifications, and opens the page of a clicked one.
 *
 * @details
 * Phase H (DES 09-10-2026, ADR-032). The desktop app's main process
 * (DESKTOP src/notifications.js) polls GET /api/push/feed and shows each item
 * as a native macOS notification, also while this window is minimised, hidden
 * or closed to the menu bar. It needs the sign-in token for that; this bridge
 * gives it on sign-in and takes it back (null) on sign-out. The token stays in
 * the main process's memory only.
 *
 * Also reports this computer's time zone once, when the account has none
 * (quiet hours and reminder times use it).
 *
 * Click: main sends the path; it is opened only when it is one of this user's
 * own pages (/app/… or /company/…).
 *
 * Not for: the widget windows (/desktop/*), superadmin (no notifications), or
 * a browser (no desktop shell).
 *
 * @author DES
 * @date 09-10-2026
 */

import { useEffect } from "react";
import { useAuth } from "@/shared/auth/auth-context";
import { isCompanyAdminRole } from "@/shared/auth/homePath";
import { tokenStorage } from "@/shared/api/tokenStorage";
import { env } from "@/shared/config/env";
import { desktopShell } from "@/shared/desktop/desktopShell";
import { router } from "@/routes";
import { browserTimeZone, fetchPushPreferences, savePushPreferences } from "./api";

export function DesktopNotificationBridge() {
  const { user, status } = useAuth();
  const isWidget = window.location.pathname.startsWith("/desktop/");
  const uid = status === "authenticated" && user && user.role !== "superadmin" ? String(user.id) : null;
  const base = user && isCompanyAdminRole(user.role) ? "/company" : "/app";

  // Sign-in hand-over (and take-back on sign-out).
  useEffect(() => {
    const shell = desktopShell();
    if (isWidget || !shell?.setNotificationAuth) return;
    const token = tokenStorage.get();
    if (!uid || !token) {
      void shell.setNotificationAuth(null);
      return;
    }
    const apiBase = env.apiBaseUrl || window.location.origin;
    void shell.setNotificationAuth({ token, userId: uid, apiBase, pathBase: base });
  }, [uid, base, isWidget]);

  // Time zone, once per sign-in, only when the account has none yet.
  useEffect(() => {
    if (isWidget || !uid || !desktopShell()?.setNotificationAuth) return;
    let live = true;
    void fetchPushPreferences()
      .then((p) => {
        const tz = browserTimeZone();
        if (live && !p.timeZone && tz) return savePushPreferences({ timeZone: tz });
        return undefined;
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [uid, isWidget]);

  // Clicked notification → its page.
  useEffect(() => {
    const shell = desktopShell();
    if (isWidget || !shell?.onNotificationOpen) return;
    return shell.onNotificationOpen((path) => {
      if (typeof path === "string" && (path === base || path.startsWith(`${base}/`))) {
        void router.navigate(path);
      }
    });
  }, [base, isWidget]);

  return null;
}
